import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Chip,
} from "@mui/material";
import { Refresh, CalendarToday } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { formatLocalDate, isOverdue } from "../utils/trelloDateUtils";

const OpenTasks = () => {
  const [openCards, setOpenCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [boardId, setBoardId] = useState(null);
  const [openListId, setOpenListId] = useState(null);
  const navigate = useNavigate();

  // Trello API configuration
  const openListName = "Client Open Tasks";
  const apiKey = process.env.REACT_APP_TRELLO_API_KEY;
  const apiToken = process.env.REACT_APP_TRELLO_API_TOKEN;
  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");

      // Fetch board ID if not available
      if (!boardId) {
        const boardResponse = await axios.get(`${apiUrl}/account/board`);
        setBoardId(boardResponse.data.board_id);
      }

      // Fetch lists if board is available
      if (boardId && !openListId) {
        const listsResponse = await axios.get(
          `https://api.trello.com/1/boards/${boardId}/lists`,
          {
            params: { key: apiKey, token: apiToken },
          }
        );

        const foundList = listsResponse.data.find(
          (list) => list.name === openListName
        );
        if (!foundList) throw new Error("Open tasks list not found");

        setOpenListId(foundList.id);
      }

      // Fetch open cards if list ID is available
      if (openListId) {
        const cardsResponse = await axios.get(
          `https://api.trello.com/1/lists/${openListId}/cards`,
          {
            params: { key: apiKey, token: apiToken },
          }
        );

        // Add formatted due date and overdue status to each card
        const cardsWithDueInfo = cardsResponse.data.map((card) => ({
          ...card,
          formattedDue: formatLocalDate(card.due),
          isOverdue: isOverdue(card.due),
        }));

        setOpenCards(cardsWithDueInfo);
      }
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || "Failed to load tasks"
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [boardId, openListId]);

  const handleRefresh = () => {
    fetchData();
  };

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Open Tasks
        </Typography>
        <Box>
          <IconButton onClick={handleRefresh} size="small" disabled={loading}>
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              <Refresh fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4, flex: 1 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && openCards.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4, flex: 1 }}>
          <Typography variant="body2" color="textSecondary">
            No open tasks
          </Typography>
        </Box>
      )}

      {/* Scrollable container for cards */}
      {!loading && openCards.length > 0 && (
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            pr: 1, // Add right padding to avoid scrollbar overlap
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.2)",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          }}
        >
          <Grid container spacing={2}>
            {openCards.map((card) => (
              <Grid item xs={12} key={card.id}>
                <Card
                  variant="outlined"
                  sx={{
                    borderLeft: "4px solid",
                    borderColor: card.isOverdue ? "error.main" : "primary.main",
                    "&:hover": {
                      cursor: "pointer",
                      backgroundColor: "action.hover",
                    },
                    transition: "background-color 0.2s",
                  }}
                  onClick={() => navigate("/tasks")}
                >
                  <CardContent>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="subtitle1" fontWeight={500}>
                        {card.name}
                      </Typography>
                      {card.isOverdue && (
                        <Chip
                          label="Overdue"
                          size="small"
                          sx={{
                            backgroundColor: "error.light",
                            color: "error.contrastText",
                            fontWeight: 600,
                            ml: 1,
                          }}
                        />
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        mb: card.due ? 1 : 0,
                      }}
                    >
                      {card.desc || "No description"}
                    </Typography>

                    {card.due && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mt: 0.5,
                          color: card.isOverdue
                            ? "error.main"
                            : "text.secondary",
                        }}
                      >
                        <CalendarToday
                          fontSize="inherit"
                          sx={{ fontSize: "1rem", mr: 0.5 }}
                        />
                        <Typography
                          variant="caption"
                          fontWeight={card.isOverdue ? 600 : "normal"}
                        >
                          {card.formattedDue}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {!loading && openCards.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => navigate("/tasks")}
          >
            View All Tasks
          </Button>
        </Box>
      )}
    </Card>
  );
};

export default OpenTasks;
